/**
 * Sprint 24 → 24.5 — AddConnectionMenu.
 *
 * The two-step Add flow: a type-picker modal. Sprint 24.5 replaces the "More
 * coming" placeholder slot with the real first-party section, driven by the
 * `list_first_party_templates` registry — Microsoft 365 (LIVE), MatterX (COMING
 * SOON, notify-me), and (in dev) Custom in-house MCP. Selecting a first-party
 * card opens the install wizard; the Custom paths open the existing MCP / Wrapped
 * flows.
 */

import type { CSSProperties } from "react";
import { ConnectionTypeChip } from "./ConnectionTypeChip";
import { FirstPartyTemplateCard } from "./first-party/FirstPartyTemplateCard";
import {
  type FirstPartyTemplate,
  useFirstPartyTemplates,
} from "../../../hooks/use-first-party-templates";

export type AddConnectionPath = "custom-mcp" | "custom-wrapped";

export interface AddConnectionMenuProps {
  open: boolean;
  onClose: () => void;
  onSelectPath: (path: AddConnectionPath) => void;
  /** Sprint 24.5 — a first-party template card was chosen (opens the install wizard). */
  onSelectTemplate: (template: FirstPartyTemplate) => void;
}

export function AddConnectionMenu({
  open,
  onClose,
  onSelectPath,
  onSelectTemplate,
}: AddConnectionMenuProps) {
  const templates = useFirstPartyTemplates();
  // Hide the entire first-party section when the registry returns nothing
  // (S24.5.22) or fails to load — the Custom paths below still work.
  const fpTemplates = templates.data ?? [];

  if (!open) return null;

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={cardModal} onClick={(e) => e.stopPropagation()}>
        <div style={headerRow}>
          <div>
            <h2 style={h2}>How do you want to connect?</h2>
            <p style={tagline}>Federate an MCP server, wrap an API, or use a first-party connector.</p>
          </div>
          <button type="button" style={closeBtn} onClick={onClose} aria-label="Close">
            <i className="ti ti-x" />
          </button>
        </div>

        {/* First-party · one-click — driven by the template registry (Sprint 24.5) */}
        {templates.isLoading ? (
          <div style={section}>
            <h3 style={h3}>First-party · one-click</h3>
            <div style={skeletonCard} />
            <div style={skeletonCard} />
          </div>
        ) : fpTemplates.length > 0 ? (
          <div style={section}>
            <h3 style={h3}>First-party · one-click</h3>
            <div style={fpList}>
              {fpTemplates.map((t) => (
                <FirstPartyTemplateCard
                  key={t.slug}
                  template={t}
                  onSelect={(tpl) => {
                    onClose();
                    onSelectTemplate(tpl);
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}

        {/* Custom */}
        <div style={section}>
          <h3 style={h3}>Custom</h3>
          <button type="button" style={pathCard} onClick={() => onSelectPath("custom-mcp")}>
            <span style={pathIcon("#FAEEDA", "#854F0B")}>
              <i className="ti ti-plug" />
            </span>
            <span style={pathBody}>
              <span style={pathTitleRow}>
                <span style={pathName}>Register an MCP server</span>
                <ConnectionTypeChip type="mcp" />
              </span>
              <span style={pathSub}>Connect any MCP-compatible server by URL — federate its tools.</span>
            </span>
            <i className="ti ti-arrow-right" style={arrow} />
          </button>

          <button type="button" style={pathCard} onClick={() => onSelectPath("custom-wrapped")}>
            <span style={pathIcon("#E1F5EE", "#0F6E56")}>
              <i className="ti ti-file-code" />
            </span>
            <span style={pathBody}>
              <span style={pathTitleRow}>
                <span style={pathName}>Wrap an OpenAPI spec</span>
                <ConnectionTypeChip type="wrapped" />
              </span>
              <span style={pathSub}>Turn any REST API with an OpenAPI spec into MCP tools.</span>
            </span>
            <i className="ti ti-arrow-right" style={arrow} />
          </button>
        </div>

        <div style={footer}>
          <button type="button" style={cancelBtn} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
};

const cardModal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  width: 480,
  maxWidth: "90vw",
  maxHeight: "88vh",
  overflowY: "auto",
  padding: "22px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 18,
  fontFamily: "var(--font-sans)",
  boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
};

const headerRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
};

const h2: CSSProperties = { fontSize: 18, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const tagline: CSSProperties = { fontSize: 12, color: "var(--color-text-secondary)", margin: "4px 0 0" };

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 16,
  padding: 2,
  flexShrink: 0,
};

const section: CSSProperties = { display: "flex", flexDirection: "column", gap: 8 };

const h3: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "var(--color-text-tertiary)",
  margin: 0,
  fontWeight: 600,
};

const fpList: CSSProperties = { display: "flex", flexDirection: "column", gap: 8 };

const skeletonCard: CSSProperties = {
  height: 76,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-secondary)",
  opacity: 0.6,
};

const pathCard: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  cursor: "pointer",
  textAlign: "left",
  width: "100%",
  fontFamily: "var(--font-sans)",
};

const pathIcon = (bg: string, fg: string): CSSProperties => ({
  width: 32,
  height: 32,
  borderRadius: "var(--border-radius-md)",
  background: bg,
  color: fg,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 15,
  flexShrink: 0,
});

const pathBody: CSSProperties = { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 };
const pathTitleRow: CSSProperties = { display: "flex", alignItems: "center", gap: 8 };
const pathName: CSSProperties = { fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" };
const pathSub: CSSProperties = { fontSize: 11, color: "var(--color-text-secondary)" };
const arrow: CSSProperties = { fontSize: 14, color: "var(--color-text-tertiary)", flexShrink: 0 };

const footer: CSSProperties = { display: "flex", justifyContent: "flex-end" };

const cancelBtn: CSSProperties = {
  padding: "6px 14px",
  fontSize: 12,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
